import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  IconCritical,
  Inline,
  Loader,
  Stack,
  Text,
} from 'braid-design-system';
import type { DropzoneInputProps } from 'react-dropzone';

import translations from '../../.vocab';

import { FileUploadIcon } from './FileUploadIcon';

interface Props {
  candidateSupportDocCountExceeded: boolean;
  dropzoneInputProps: DropzoneInputProps;
  errorMessage?: string;
  onAddAttachmentButtonClick: () => void;
  uploading: boolean;
}

export const DropZoneContent = ({
  candidateSupportDocCountExceeded,
  errorMessage,
  dropzoneInputProps,
  onAddAttachmentButtonClick,
  uploading,
}: Props) => {
  const { t } = useTranslations(translations);

  if (uploading) {
    return <Loader size="standard" />;
  }

  if (candidateSupportDocCountExceeded) {
    return (
      <Box padding="large">
        <Text tone="secondary" align="center">
          {t(
            'You have reached your 20 attachments limit. Please delete some files below to upload a new one.',
          )}
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="center" marginBottom="small">
        <FileUploadIcon />
      </Box>
      <Stack space="xxsmall" align="center">
        <Stack space="small" align="center">
          <Inline space="none" align="center" alignY="center">
            <input {...dropzoneInputProps} data-testid="upload-file-input" />
            <Text align="center" weight="strong" size="small">
              {t('Choose a file or drag & drop it here.')}
            </Text>
          </Inline>
          <Box paddingX="small">
            {errorMessage ? (
              <Text tone="critical" align="center" size="small">
                <IconCritical /> {errorMessage}
              </Text>
            ) : (
              <Box maxWidth="small" paddingX="medium">
                <Text tone="secondary" align="center" size="small">
                  {t(
                    'Accepted file types: .jpeg, .png, .pdf, .gif, .bmp, .msg, .txt, .doc, .docx, .html, .rtf up to 4 MB. Add up to 20 attachments.',
                  )}
                </Text>
              </Box>
            )}
          </Box>
        </Stack>
        <Box display="flex" alignItems="center">
          <Button
            tone="formAccent"
            variant="transparent"
            onClick={onAddAttachmentButtonClick}
            size="small"
            data={{
              testid: 'add-attachment-button',
            }}
          >
            {t('Browse file')}
          </Button>
        </Box>
      </Stack>
    </>
  );
};
